import { useEffect, useMemo, useState } from 'react';
import {
    BlockingLoader,
    Button,
    ButtonVariant,
    Card,
    Chip,
    ChipGroup,
    ChipType,
    ClaimDetails,
    ClaimLifecycle,
    ClaimRequestNomaRepository,
    ClaimRequestStatus,
    ClaimRequestType,
    ClinicalHistoryCardStyle,
    ClinicalHistoryWrapper,
    CreationNote,
    DatePicker,
    DEFAULT_NOTE_AUDIENCE,
    IncidentNotes,
    Note,
    NoteScope,
    NoteTypeValue,
    ParentCard,
    PetProfile,
    Policy,
    PolicyCard,
    RequestDetailHeader,
    SupportingDocumentType,
    useClaimRequest as useClaimRequestWithRepository,
    useClaimRequestStatus,
    useClinicalHistory,
    useContractIdsActiveOnClaimRequest as useContractIdsActiveOnClaimRequestWithRepository,
    useFraudAlerts,
    useNotes,
    useReservation as useReservationWithRepository,
    useHref,
    DuplicateClaimConfirmationDialog,
} from '@barkibu/noma-commons';
import { SupportingDocument, SupportingDocuments, Invoice, InvoiceNomaRepository } from '@barkibu/noma-data-entry';
import { useClaimRequestSummary as useClaimRequestSummaryWithRepository } from '../../../application/ClaimRequestSummary/useClaimRequestSummary';
import { ClaimRequestSummary } from '../../../domain/ClaimRequestSummary';
import { ClaimRequestSummaryNomaRepository } from '../../../infrastructure/ClaimRequestSummary/ClaimRequestSummaryNomaRepository';
import { useModuleTranslation } from '../../../utils/useModuleTranslation';
import { BillableConceptSummaryTable, ClaimSummaryCard } from '../../components/organisms';
import './summary-view-page.modules.scss';

export type SummaryViewPageProps = {
    claimRequestKey: string;
};

const SummaryViewPage = ({ claimRequestKey }: SummaryViewPageProps) => {
    const useClaimRequestSummary = useMemo(
        () => useClaimRequestSummaryWithRepository(ClaimRequestSummaryNomaRepository),
        []
    );
    const useReservation = useMemo(() => useReservationWithRepository(ClaimRequestNomaRepository), []);
    const useClaimRequest = useMemo(() => useClaimRequestWithRepository(ClaimRequestNomaRepository), []);
    const useContractIdsActiveOnClaimRequest = useMemo(
        () => useContractIdsActiveOnClaimRequestWithRepository(ClaimRequestNomaRepository),
        []
    );
    const { claimRequestSummary } = useClaimRequestSummary(claimRequestKey);
    const { hasStatusReservable } = useClaimRequestStatus(claimRequestKey);
    const { contractIdsActiveOnClaimRequest } = useContractIdsActiveOnClaimRequest(claimRequestKey);
    const { overturnVetAnalysisDecision, duplicateClaimRequest, bindReferralApproval } =
        useClaimRequest(claimRequestKey);
    const { stealReservation } = useReservation();
    const [summary, setSummary] = useState<ClaimRequestSummary | undefined>(claimRequestSummary);
    const [policy, setPolicy] = useState<Policy | undefined>();
    const { clinicalHistory } = useClinicalHistory(claimRequestSummary?.policyIdentifier);
    const [documents, setDocuments] = useState<SupportingDocument[]>(claimRequestSummary?.supportingDocuments || []);
    const { t } = useModuleTranslation();
    const readOnly = true;
    const [loading, setLoading] = useState(false);
    const { fraudAlerts } = useFraudAlerts(claimRequestKey);
    const [currentUserReservation, setCurrentUserReservation] = useState(claimRequestSummary?.userReservation);
    const [showDuplicateClaimConfirmationDialog, setShowDuplicateClaimConfirmationDialog] = useState(false);

    const [invoices, setInvoices] = useState<Invoice[]>([]);
    const invoiceRepository = useMemo(() => new InvoiceNomaRepository(), []);

    useEffect(() => {
        if (summary?.incidentKey) {
            invoiceRepository
                .findByIncidentKey(summary.incidentKey)
                .then(setInvoices)
                .catch(() => setInvoices([]));
        }
    }, [summary?.incidentKey, invoiceRepository]);

    const invoiceDates = invoices.filter((inv) => inv.date != null).map((inv) => inv.date!);

    const {
        notes: policyNotes,
        createNote: addPolicyNote,
        deleteNote: deletePolicyNote,
    } = useNotes(NoteScope.POLICY, summary?.key ?? undefined, summary?.policyIdentifier ?? undefined);
    const {
        notes: petParentNotes,
        createNote: addPetParentNote,
        deleteNote: deletePetParentNote,
    } = useNotes(NoteScope.PET_PARENT, summary?.key ?? undefined, summary?.policyIdentifier ?? undefined);
    const {
        notes: petNotes,
        createNote: addPetNote,
        deleteNote: deletePetNote,
    } = useNotes(NoteScope.PET, summary?.key ?? undefined, summary?.policyIdentifier ?? undefined);
    const handleAddNote = (note: CreationNote) => {
        switch (note.scope) {
            case NoteScope.POLICY:
                addPolicyNote(note);
                break;
            case NoteScope.PET:
                addPetNote(note);
                break;
            case NoteScope.PET_PARENT:
                addPetParentNote(note);
                break;
        }
    };
    const handleDeleteNote = (note: Note) => {
        switch (note.scope) {
            case NoteScope.POLICY:
                deletePolicyNote(note.key);
                break;
            case NoteScope.PET:
                deletePetNote(note.key);
                break;
            case NoteScope.PET_PARENT:
                deletePetParentNote(note.key);
                break;
        }
    };

    const claimSummaries = (summary?.notes ?? [])
        .filter((note) => note.type === NoteTypeValue.CLAIM_SUMMARY)
        .map((note) => ({
            audience: note.audience ?? DEFAULT_NOTE_AUDIENCE,
            description: note.description,
            updatedAt: note.updatedAt,
        }));

    const handleSaveClaimSummary = async (content: string, audience: string) => {
        if (!summary) return;
        const previousNotes = summary.notes;
        // Replace only the note for the audience being edited; other audiences are left untouched.
        const otherNotes = previousNotes.filter(
            (note) => note.type !== NoteTypeValue.CLAIM_SUMMARY || (note.audience ?? DEFAULT_NOTE_AUDIENCE) !== audience
        );
        const editedNote = previousNotes.find(
            (note) => note.type === NoteTypeValue.CLAIM_SUMMARY && (note.audience ?? DEFAULT_NOTE_AUDIENCE) === audience
        );
        const optimisticNote: Note = {
            key: editedNote?.key ?? `optimistic-claim-summary-${audience}`,
            type: NoteTypeValue.CLAIM_SUMMARY,
            description: content,
            scope: NoteScope.CLAIM_REQUEST,
            updatedAt: new Date(),
            audience,
        };
        setSummary({ ...summary, notes: [optimisticNote, ...otherNotes] });
        try {
            const savedNote = await ClaimRequestSummaryNomaRepository.updateClaimSummary(
                claimRequestKey,
                content,
                audience
            );
            setSummary((current) =>
                current
                    ? {
                          ...current,
                          notes: [
                              savedNote,
                              ...current.notes.filter(
                                  (n) =>
                                      n.type !== NoteTypeValue.CLAIM_SUMMARY ||
                                      (n.audience ?? DEFAULT_NOTE_AUDIENCE) !== audience
                              ),
                          ],
                      }
                    : current
            );
        } catch (error) {
            setSummary((current) => (current ? { ...current, notes: previousNotes } : current));
            throw error;
        }
    };

    const handleDuplicateClaimRequest = async () => {
        const duplicatedClaimRequest = await duplicateClaimRequest();
        toDataEntry(duplicatedClaimRequest.key);
    };

    useEffect(() => {
        setSummary(claimRequestSummary);
        setPolicy(
            claimRequestSummary?.pet?.policies.find((p) => p.identifier == claimRequestSummary?.policyIdentifier)
        );
        const paymentLetterSupportingDocument = claimRequestSummary
            ? claimRequestSummary.paymentLetters.map(
                  (paymentLetter: string) =>
                      new SupportingDocument({ type: SupportingDocumentType.PAYMENT_LETTER, location: paymentLetter })
              )
            : [];
        setDocuments([...(claimRequestSummary?.supportingDocuments || []), ...paymentLetterSupportingDocument]);
        setCurrentUserReservation(claimRequestSummary?.userReservation);
    }, [claimRequestSummary]);

    const handleStealReservation = () => {
        stealReservation(claimRequestKey).then((user) => setCurrentUserReservation(user));
    };

    const { toDataEntry } = useHref();
    const handleOverturnVetDecision = async () => {
        await overturnVetAnalysisDecision();
        toDataEntry(claimRequestKey);
    };

    const handleBindReferralApproval = async (file?: File) => {
        try {
            await bindReferralApproval(file);
            window.location.reload();
        } catch (error) {
            alert(`Error binding referral approval: ${error}`);
        }
    };

    return (
        <>
            {!loading && summary?.pet ? (
                <div className="summary-view">
                    <section className="summary-view__pet-details">
                        <PetProfile
                            name={summary.pet?.name}
                            birthDate={summary.pet?.birthDate}
                            breed={summary.pet?.breed}
                            gender={summary.pet?.gender}
                            chip={summary.pet?.chip}
                            photo={summary.pet?.photo}
                            totalClaimRequestCount={summary.pet?.totalClaimRequestCount}
                            openClaimRequestCount={summary.pet?.openClaimRequestCount}
                            tags={petNotes
                                .filter((note) => note.type == NoteTypeValue.TAG)
                                .map((petNote) => petNote.description)}
                        />
                        <PolicyCard
                            setLoading={setLoading}
                            fraudAlerts={fraudAlerts}
                            policyIdentifier={summary.policyIdentifier}
                            countryCode={summary.countryCode}
                            policies={summary.pet.policies}
                            petName={summary.pet.name}
                            tags={policyNotes
                                .filter((note) => note.type == NoteTypeValue.TAG)
                                .map((policyNote) => policyNote.description)}
                            contractIdsActiveOnClaimRequest={contractIdsActiveOnClaimRequest}
                            invoiceDates={invoiceDates}
                            totalInvoiceCount={invoices.length}
                        />
                        <ParentCard
                            readOnly
                            email={summary.pet?.petParent?.email ?? ''}
                            iban={summary.iban ?? ''}
                            hideIban={summary.prepaidExpenseIds?.length > 0}
                            tags={petParentNotes
                                .filter((note) => note.type == NoteTypeValue.TAG)
                                .map((petParentNote) => petParentNote.description)}
                        />
                    </section>
                    <section className="summary-view__request-details">
                        {claimRequestSummary?.key && (
                            <RequestDetailHeader
                                userReservation={currentUserReservation}
                                date={summary?.createdAt}
                                seePreviousUrl={`/dataentry/policy/${policy?.identifier}/incidents`}
                                onStealReservation={
                                    hasStatusReservable(summary.status) ? () => handleStealReservation() : undefined
                                }
                                policyNotes={policyNotes}
                                petNotes={petNotes}
                                petParentNotes={petParentNotes}
                                onAddNote={handleAddNote}
                                onDeleteNote={handleDeleteNote}
                                prepaidExpenseIds={claimRequestSummary.prepaidExpenseIds}
                            />
                        )}
                    </section>
                    <div className="summary-view__info-container">
                        <div className="summary-view__info-container__left-container">
                            <section className="summary-view__info-container__status">
                                <ClaimLifecycle
                                    status={summary.status}
                                    claimRequestKey={claimRequestKey}
                                    statusDetails={summary.statusDetails}
                                    onBindReferralApproval={handleBindReferralApproval}
                                />
                            </section>
                            <section className="summary-view__info-container__dates">
                                <Card title={t('summary_view.dates')}>
                                    <DatePicker
                                        value={summary.originalDateOfLoss}
                                        label={t('summary_view.origin') || undefined}
                                        disabled
                                    />
                                </Card>
                            </section>
                            {summary.diagnosis.length > 0 && (
                                <section className="summary-view__info-container__symptoms">
                                    <Card title={t('summary_view.diagnoses')}>
                                        <ChipGroup>
                                            {summary.diagnosis.map((el) => (
                                                <Chip
                                                    key={`${el.key}`}
                                                    text={`${el.description}`}
                                                    type={ChipType.NEUTRAL}
                                                    className={'summary-view__chip-multiline'}
                                                />
                                            ))}
                                        </ChipGroup>
                                    </Card>
                                </section>
                            )}
                            {summary.symptoms.length > 0 && (
                                <section className="summary-view__info-container__diagnosis">
                                    <Card title={t('summary_view.symptoms')}>
                                        <ChipGroup>
                                            {summary.symptoms.map((el) => (
                                                <Chip
                                                    key={el.key}
                                                    text={el.description}
                                                    type={ChipType.NEUTRAL}
                                                    className={'multiline'}
                                                />
                                            ))}
                                        </ChipGroup>
                                    </Card>
                                </section>
                            )}
                            {summary.type == ClaimRequestType.HEALTH && (
                                <ClaimDetails
                                    visitReason={summary.visitReason}
                                    causeOfLoss={summary.causeOfLoss}
                                    readOnly
                                />
                            )}
                            <section className="summary-view__info-container__claim-summary">
                                <ClaimSummaryCard summaries={claimSummaries} onSave={handleSaveClaimSummary} />
                            </section>
                            <section className="summary-view__info-container__notes">
                                <IncidentNotes notes={summary.notes} readOnly={readOnly} />
                            </section>
                        </div>

                        <div className="summary-view__info-container__right-container">
                            <section>
                                <ClinicalHistoryWrapper
                                    clinicalHistory={clinicalHistory}
                                    policyIdentifier={summary.policyIdentifier}
                                    style={ClinicalHistoryCardStyle.BIG}
                                />
                            </section>
                            <section>
                                <SupportingDocuments
                                    documents={documents}
                                    allowEdit={false}
                                    showFooter={false}
                                    invoices={invoices}
                                    claimRequestKey={claimRequestKey}
                                />
                            </section>
                            <section>
                                <BillableConceptSummaryTable
                                    title={'Cost Lines'}
                                    invoiceSummaryList={summary.invoices}
                                />
                            </section>
                        </div>

                        <div className="summary-view__info-container__actions">
                            {(summary.status == ClaimRequestStatus.VET_ANALYSIS_COMPLETED ||
                                summary.status == ClaimRequestStatus.RESOLUTION_COMPLETED ||
                                summary.status == ClaimRequestStatus.DISBURSEMENT_REGISTERED_IN_PAS ||
                                summary.status == ClaimRequestStatus.DISBURSEMENT_ACCEPTED_BY_PAS) && (
                                <>
                                    {summary.status == ClaimRequestStatus.VET_ANALYSIS_COMPLETED && (
                                        <Button
                                            variantName={ButtonVariant.SECONDARY}
                                            text={t('summary_view.overturn_vet_decision')}
                                            onClick={handleOverturnVetDecision}
                                        ></Button>
                                    )}
                                </>
                            )}
                            <Button
                                variantName={ButtonVariant.SECONDARY}
                                text={t('summary_view.duplicate_claim')}
                                onClick={() => setShowDuplicateClaimConfirmationDialog(true)}
                            ></Button>
                            <DuplicateClaimConfirmationDialog
                                isOpen={showDuplicateClaimConfirmationDialog}
                                onSave={() => {
                                    setShowDuplicateClaimConfirmationDialog(false);
                                    handleDuplicateClaimRequest();
                                }}
                                onCancel={() => setShowDuplicateClaimConfirmationDialog(false)}
                            />
                        </div>
                    </div>
                </div>
            ) : (
                <BlockingLoader />
            )}
        </>
    );
};

export default SummaryViewPage;
